import { Component, ElementRef, HostBinding, Input, OnDestroy, OnInit, ViewChild, inject, input } from '@angular/core';
import { FormControl, NgControl, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { UpdateFormHasBeenInView } from '@app/sales/states/form/form.actions';
import { IconNames } from '@app/shared/enums/icon-names';
import { Store } from '@ngxs/store';
import Moment from 'moment';
import { MatIcon } from '@angular/material/icon';
import { IceIconButtonComponent } from '../../atoms/ice-icon-button/ice-icon-button.component';
import { MatDatepickerInput, MatDatepicker } from '@angular/material/datepicker';
import { MatInput } from '@angular/material/input';
import { NgClass } from '@angular/common';
import { getObjectPropertyFromPath } from '@app/core/utils';

/**
 * 'ngDefaultControl' and 'formControlName' must be provided to the component.
 *
 * Usage example as standalone input:
 * @example
 * <app-ice-date-picker ngDefaultControl formControlName="myFormControlName" label="myLabel" />
 *
 * Usage example as part of a form:
 * @example
 * <app-ice-date-picker ngDefaultControl formControlName="myFormControlName" label="myLabel" ngxsFormPath="stateForm.myForm" />
 */
@Component({
  selector: 'app-ice-date-picker',
  templateUrl: './ice-date-picker.component.html',
  styleUrl: './ice-date-picker.component.scss',
  imports: [
    NgClass,
    MatInput,
    FormsModule,
    MatDatepickerInput,
    ReactiveFormsModule,
    IceIconButtonComponent,
    MatDatepicker,
    MatIcon,
  ],
})
export class IceDatePickerComponent implements OnInit, OnDestroy {
  private controlDirective = inject(NgControl);
  private store = inject(Store);

  IconNames = IconNames;

  @ViewChild('inputElement') inputElement: ElementRef;

  // Label
  @Input({ required: true }) label: string;
  @Input() labelHelpText: string;
  @Input() forId = '';

  // Information
  @Input() errorText: string;
  @Input() longHelpText: string;
  @Input() hideOptionalText: boolean = false;

  // Input
  @Input() inputWidth: 'x-small' | 'small' | 'medium' | 'large' | '' = '';
  @Input() placeholder = '';
  @Input() readonly = false;
  @Input() ngxsFormPath: string;

  // Errorstate
  @Input() errorOnTouchedOnly: boolean = false;

  // Datepicker
  @Input() minDate: Date;
  @Input() maxDate: Date;
  @Input() allowedDates: Date[];

  // Testing
  textTestId = input.required<string>();
  buttonTestId = input.required<string>();

  isDatePickerOpen: boolean = false;
  isDateAllowedFilterFn: (pickerDate: Date) => boolean;

  @HostBinding('class') get class() {
    return this.inputWidth;
  }

  control: FormControl;

  get isDisabled(): boolean {
    return this.control.disabled;
  }

  get isRequired(): boolean {
    return this.control.hasValidator(Validators.required);
  }

  get showIsRequiredError(): boolean {
    return this.control.hasError('required') && this.isFormVisitedOrControlTouched();
  }

  get isErrorState(): boolean {
    return (
      (this.control.invalid && !!this.control.value && (this.control.touched || this.isFormVisitedOrControlTouched())) ||
      this.showIsRequiredError
    );
  }

  private hasFormBeenVisited = false;

  ngOnInit() {
    this.control = this.controlDirective.control as FormControl;

    if (!!this.ngxsFormPath) {
      const state = this.store.snapshot();
      const contextForm = getObjectPropertyFromPath(state, this.ngxsFormPath);
      this.hasFormBeenVisited = contextForm?.dirty;
    }

    this.isDateAllowedFilterFn = (pickerDate: Date): boolean => {
      if (this.allowedDates?.length > 0) {
        return this.allowedDates?.some(
          allowedDate => new Date(allowedDate).toDateString() == new Date(pickerDate).toDateString()
        );
      } else if (this.minDate && this.maxDate) {
        return Moment(pickerDate).isBetween(this.minDate, this.maxDate, 'day', '[]');
      } else {
        return true;
      }
    };
  }

  isFormVisitedOrControlTouched(): boolean {
    return this.errorOnTouchedOnly ? this.control.touched : this.hasFormBeenVisited;
  }

  toggleIsDatePickerOpen(): void {
    this.isDatePickerOpen = !this.isDatePickerOpen;
  }

  // Support manually inputted dates
  // Make sure to use the same date type as the datepicker
  onDateInputChanged(dateString): void {
    if (dateString.target.value) {
      const momentDate = Moment(dateString.target.value, 'DD.MM.YYYY', true);

      if (momentDate.isValid()) {
        this.control.setValue(momentDate);
      } else {
        const momentDateShort = Moment(dateString.target.value, 'DDMMYY', true);
        this.control.setValue(momentDateShort.isValid() ? momentDateShort : null);
      }
    }
  }

  ngOnDestroy() {
    if (!!this.ngxsFormPath) {
      this.store.dispatch(new UpdateFormHasBeenInView(this.ngxsFormPath));
    }
  }
}
